import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * RadioGroup — a single-choice control on the Radix base
 * (`@radix-ui/react-radio-group`): `role="radiogroup"` with roving-tabindex
 * arrow-key navigation, full ARIA, and controlled (`value`/`onValueChange`) or
 * uncontrolled (`defaultValue`) selection. Compose: `RadioGroup` (root) wrapping
 * one `RadioGroupItem` per option, each with a unique `value`.
 *
 * Two token-only variants, chosen on the root and shared with every item via
 * context:
 * - `plain` (default) — a 16px control matching the Checkbox metrics; the
 *   checked state is a 5px `--brand` border whose negative space reads as the
 *   filled dot (no separate indicator element). Hover deepens an unchecked
 *   border a step (`--border-hover`); `aria-invalid` (on the group or an item)
 *   repaints the border with `--input-invalid`.
 * - `card` — each option is a selectable bordered container holding the same dot
 *   control beside its label; the checked option gets a `--brand` border and a
 *   faint brand `/.04` wash.
 *
 * Tokens only — no hardcoded colors or radii.
 */

type RadioVariant = 'plain' | 'card'

const RadioGroupContext = React.createContext<{
	variant: RadioVariant
	invalid: boolean
}>({ variant: 'plain', invalid: false })

function RadioGroup({
	className,
	variant = 'plain',
	'aria-invalid': ariaInvalid,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & {
	variant?: RadioVariant
}) {
	const invalid = ariaInvalid === true || ariaInvalid === 'true'
	return (
		<RadioGroupContext.Provider value={{ variant, invalid }}>
			<RadioGroupPrimitive.Root
				data-slot="radio-group"
				aria-invalid={ariaInvalid}
				className={cn('grid gap-2', className)}
				{...props}
			/>
		</RadioGroupContext.Provider>
	)
}

/**
 * The shared 16px circle — its 1px resting border thickens to a 5px `--brand`
 * ring when checked, whose negative space reads as the filled dot. The
 * checked-state selector differs by variant (the plain item is itself the
 * circle; the card's dot is a child of the item), so each caller appends its
 * own checked rule.
 */
const circleBase =
	'bg-background size-4 shrink-0 rounded-full border border-input transition-[border-width,border-color]'

function RadioGroupItem({
	className,
	children,
	'aria-invalid': ariaInvalid,
	...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
	const { variant, invalid: groupInvalid } = React.useContext(RadioGroupContext)
	// Item-level aria-invalid wins; otherwise inherit the group's invalid state so
	// a single `aria-[invalid]` class covers both.
	const invalid = ariaInvalid ?? (groupInvalid || undefined)

	if (variant === 'card') {
		return (
			<RadioGroupPrimitive.Item
				data-slot="radio-group-item"
				aria-invalid={invalid}
				className={cn(
					'group focus-cosy border-input relative flex w-full items-start gap-3 rounded-lg border p-4 text-left outline-hidden transition-[border-color,background-color]',
					'data-[state=checked]:border-brand data-[state=checked]:bg-[oklch(from_var(--brand)_l_c_h/0.04)]',
					'aria-[invalid]:border-input-invalid',
					'disabled:cursor-not-allowed disabled:opacity-50',
					className,
				)}
				{...props}
			>
				{/* Decorative dot mirroring the item's checked state via `group-data`. */}
				<span
					aria-hidden
					className={cn(
						circleBase,
						'mt-0.5 group-data-[state=checked]:border-[5px] group-data-[state=checked]:border-brand',
					)}
				/>
				{children ? <span className="min-w-0 flex-1">{children}</span> : null}
			</RadioGroupPrimitive.Item>
		)
	}

	// Plain: the item IS the circle (Checkbox metrics); the negative space left by
	// the 5px checked border reads as the filled dot.
	return (
		<RadioGroupPrimitive.Item
			data-slot="radio-group-item"
			aria-invalid={invalid}
			className={cn(
				circleBase,
				'focus-cosy outline-hidden',
				'data-[state=unchecked]:enabled:hover:border-[var(--border-hover)]',
				'data-[state=checked]:border-[5px] data-[state=checked]:border-brand',
				'aria-[invalid]:border-input-invalid',
				'disabled:cursor-not-allowed disabled:opacity-50',
				className,
			)}
			{...props}
		/>
	)
}

export { RadioGroup, RadioGroupItem }
